/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import axios from 'axios';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { EditToggleButton } from './Buttons';
import { useAuth } from '../../services/cognito';
import {
  KakaoAddress,
  Layer,
  LatLng,
  Building,
  VisitStatus,
} from '../../types';
import Title from '../dashboard/Title';
import { LayerEditableMap, getCentroidOfMap } from '../layers/LayerEditableMap';
import config from '../../config';
import { TerritoryInfo } from './TerritoryInfo';
import { HouseHoldButton } from './HouseHold';
import { ConfirmDialog } from '../utils/AlertDialog';
import { LiveCardProvider, LiveViewersBanner } from '../../services/liveCard';

export function TerritoryMap(props: any) {
  const { layer1, layer2 } = props;
  const { isAdmin, token } = useAuth();

  const [currAddress, setCurrAddress] = React.useState<KakaoAddress>(
    {} as KakaoAddress,
  );
  const [resetConfirmOpen, setResetConfirmOpen] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [resetSnackbar, setResetSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [editable, setEditable] = React.useState(false);
  const [parent, setParent] = React.useState<Layer>({} as Layer);
  const [layers, setLayers] = React.useState<Layer[]>([]);
  const [buildings, setBuildings] = React.useState<Building[]>([]);
  const [center, setCenter] = React.useState<LatLng>({
    lat: config.map.defaultCentroid.lat,
    lng: config.map.defaultCentroid.lng,
  });
  const [title, setTitle] = React.useState('');
  const [subTitle, setSubTitle] = React.useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(-1);

  const onCurrentAddrChanged = (addr: KakaoAddress) => {
    setCurrAddress(addr);
  };

  const handleToggle = () => {
    setEditable(!editable);
  };

  const getLayers = async () => {
    try {
      const { data } = await axios.get(
        `${config.api.url}/mdsouth/layers/${layer1}/${layer2}`,
      );
      setParent(data.parent);
      setLayers(data.layers);
      setBuildings(data.layers[0].buildings);
      setCenter(getCentroidOfMap(data.layers));

      const title = `${layer1}${layer2}`;
      const subTitle =
        data.layers[0] === undefined
          ? data.layers[0].name
          : data.layers[0].description;

      setTitle(title);
      setSubTitle(subTitle);
    } catch (error) {
      console.log(error);
    }
  };

  const handleResetCard = (answer: string) => async () => {
    setResetConfirmOpen(false);
    if (answer !== 'confirm') return;
    setResetting(true);
    try {
      const { data } = await axios.post(
        `${config.api.url}/mdsouth/reset/${layer1}/${layer2}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await getLayers();
      setResetSnackbar({
        open: true,
        message: `초기화 완료: 세대 ${data.householdsReset}건, 방문자기록 ${data.visitorRecordsDeleted}건 (방문금지 유지)`,
        severity: 'success',
      });
    } catch (error) {
      setResetSnackbar({
        open: true,
        message: '초기화에 실패했습니다',
        severity: 'error',
      });
    } finally {
      setResetting(false);
    }
  };

  const handleCenterChanged = (center: LatLng) => {
    setCenter(center);
  };

  const handleBuildingDelete = (id: number) => () => {
    setSelectedId(id);
    setOpenConfirmDialog(true);
  };

  const handleConfirmDialogClose = (answer: string) => async () => {
    switch (answer) {
      case 'confirm':
        try {
          const id = selectedId;
          await axios.delete(`${config.api.url}/mdsouth/buildings/${id}`);
          setBuildings(
            buildings.filter(building => {
              return building.id !== id;
            }),
          );
        } catch (error) {
          console.log(error);
        }
        break;
      case 'cancel':
      default:
        break;
    }
    setOpenConfirmDialog(false);
  };

  const handleBuildingAdd = async () => {
    try {
      const { data } = await axios.post(`${config.api.url}/mdsouth/buildings`, {
        name: '',
        address: '',
        layerId: layers[0].id,
      });
      setBuildings([
        ...buildings,
        {
          id: data.id,
          name: '',
          address: '',
          gates: [],
          layerId: layers[0].id,
        },
      ]);
    } catch (error) {
      console.log(error);
    }
  };

  const handleBuildingUpdate = async (
    id: number,
    name: string,
    address: string,
  ) => {
    try {
      await axios.put(`${config.api.url}/mdsouth/buildings/${id}`, {
        name,
        address,
        layerId: layers[0].id,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const layerDescriptionChanged = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const text = e.target.value;
    await axios.put(`${config.api.url}/mdsouth/layers/${layers[0].id}`, {
      level: layers[0].level,
      name: layers[0].name,
      description: text,
      polygonPath: layers[0].polygonPath,
      centroid: layers[0].centroid,
      parentName: parent.name,
    });
    setSubTitle(text);
  };

  React.useEffect(() => {
    getLayers();
  }, []);

  return (
    <LiveCardProvider layer1={layer1} layer2={layer2} onReset={getLayers}>
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <LiveViewersBanner />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h3" gutterBottom>
          {title}
          <EditToggleButton visible isLock={!editable} onClick={handleToggle} />
        </Typography>
      </Grid>
      {isAdmin && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<RestartAltIcon />}
            disabled={resetting}
            onClick={() => setResetConfirmOpen(true)}
          >
            이 구역카드 초기화
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        {!editable ? (
          <Title>{subTitle}</Title>
        ) : (
          <TextField
            size="small"
            variant="standard"
            id="subtitle-edit"
            onChange={layerDescriptionChanged}
            value={subTitle}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <LayerEditableMap
          editable={editable}
          mapLevel={2}
          parent={parent}
          layerLevel={2}
          layers={layers}
          setLayers={setLayers}
          center={center}
          handleCenterChanged={handleCenterChanged}
          maxNumLayers={1}
          onCurrentAddrChanged={onCurrentAddrChanged}
          apiEndpoint={`${config.api.url}/mdsouth/layers/${layer1}/${layer2}`}
        />
        <Typography variant="body2" gutterBottom>
          방문 표시 방법: 버튼을 눌러 방문기록을 하고 저장해주세요. 한번 누르면
          방문했지만 집주인을 만나지 못한 경우를 표시하고, 두 번 누르면 방문하고
          집주인도 만났음을 표시합니다. 세번 누르면 방문하지 않은 초기 상태로
          돌아옵니다.
        </Typography>
        <Typography variant="body2" gutterBottom>
          구역조사를 하는 경우: 새주소 번호를 기준으로 대문/뒷문/쪽문 으로 입구
          정보를 기록해 주세요. 아파트 형태라면 1-2 (1-2 호 라인) 혹은 1-4 (1-4
          호 라인)으로 표시합니다. 한 입구에서 접근가능한 모든 집을 층과 호수로
          구분합니다. 호수가 구분되지 않는경우, 자연스럽게 방문이 가능한 순서로
          번호를 정하시고 마주보아 같은 거리인 경우 왼쪽을 1호로 합니다.
        </Typography>
        <Typography variant="body2" gutterBottom>
          방문을 하는 경우 방문 상태에 따른 버튼 표시는 다음과 같습니다. 한번도
          방문하지 않은 처음 상태는 `미방문` 버튼입니다. 방문을 했지만 집주인을
          만나지 못했다면 한번 눌러 `재방필요` 로 만들어 주세요. 집주인을
          만났다면 두 번눌러 `만났음` 상태로 만들어 주시면 됩니다.
        </Typography>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1, borderBottom: 1 }}
        >
          <HouseHoldButton
            household={{
              id: 0,
              name: '미방문',
              status: VisitStatus.intact,
              gateId: -1,
            }}
            size="small"
          />
          <HouseHoldButton
            household={{
              id: 0,
              name: '재방필요',
              status: VisitStatus.visited,
              gateId: -1,
            }}
            size="small"
          />
          <HouseHoldButton
            household={{
              id: 0,
              name: '만났음',
              status: VisitStatus.met,
              gateId: -1,
            }}
            size="small"
          />
          <HouseHoldButton
            household={{
              id: 0,
              name: '방문금지',
              status: VisitStatus.noVisit,
              gateId: -1,
            }}
            size="small"
          />
          <Typography
            variant="body2"
            gutterBottom
            color="secondary"
            sx={{ pt: 2 }}
          >
            2 초 이상 버튼을 누르면, 세대명 혹은 상호명을 수정할 수 있습니다.
            방문금지를 표시할 때도 마찬가지 입니다.
          </Typography>
        </Grid>
      </Grid>
      <TerritoryInfo
        buildings={buildings}
        editable={editable}
        currAddress={currAddress}
        handleBuildingAdd={handleBuildingAdd}
        handleBuildingUpdate={handleBuildingUpdate}
        handleBuildingDelete={handleBuildingDelete}
      />
      <ConfirmDialog
        open={openConfirmDialog}
        title="빌딩 삭제"
        message="이 빌딩과 모든 세대정보를 지웁니다, 맞습니까?"
        handleClose={handleConfirmDialogClose}
      />
      <ConfirmDialog
        open={resetConfirmOpen}
        title="구역카드 초기화"
        message={`${layer1}-${layer2} 카드의 방문 상태와 방문자 기록을 초기화합니다. 방문금지 세대는 그대로 유지됩니다. 진행할까요?`}
        handleClose={handleResetCard}
      />
      <Snackbar
        open={resetSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setResetSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={resetSnackbar.severity}
          onClose={() => setResetSnackbar(s => ({ ...s, open: false }))}
        >
          {resetSnackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
    </LiveCardProvider>
  );
}
